
import { supabase } from './supabaseClient';
import { PublicDataPoint } from '../types';

// Hardcoded fallback keys to match supabaseClient.ts configuration
const PROJECT_URL = (import.meta as any).env.VITE_SUPABASE_URL || 'https://gdjfeumtqpjbroxqbfqv.supabase.co';
const ANON_KEY = (import.meta as any).env.VITE_SUPABASE_ANON_KEY || 'sb_publishable_qZLqtJBltKyNGhQoxYK_og_5z3vcp2P';

interface FetchPublicDataParams {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
  sourceId?: string;
}

export const fetchPublicDataPoints = async ({ 
  page = 1, 
  limit = 20, 
  search = '', 
  categoryId = '', 
  sourceId = '' 
}: FetchPublicDataParams = {}) => {
  
  // Calculate range for Supabase (0-based index)
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  let query = supabase
    .from('public_data_points')
    .select(`
        *,
        source:data_sources(name),
        category:data_categories(name)
    `, { count: 'exact' }); // Request exact count for pagination

  // Apply Filters
  if (categoryId) {
    query = query.eq('category_id', parseInt(categoryId));
  }

  if (sourceId) {
    query = query.eq('source_id', sourceId);
  }

  if (search) {
    // Search in summary_text OR unit (using simple OR logic)
    query = query.or(`summary_text.ilike.%${search}%,unit.ilike.%${search}%`);
  }

  // Apply Pagination & Sorting
  const { data, error, count } = await query
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) throw error;
  
  return { 
    data: data as PublicDataPoint[], 
    count: count || 0 
  };
};

export const updatePublicDataPoint = async (id: string, updates: Partial<PublicDataPoint>) => {
  const { data, error } = await supabase
    .from('public_data_points')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as PublicDataPoint;
};

export const deletePublicDataPoint = async (id: string) => {
  const { error } = await supabase
    .from('public_data_points')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const bulkInsertDataPoints = async (dataPoints: Partial<PublicDataPoint>[]) => {
    console.log(`[BulkInsert] Service received ${dataPoints.length} items.`);

    // 1. Validate Data
    const validPoints = dataPoints.filter((p, index) => {
        // Ensure source_id is treated as a string even if it comes as number
        if (p.source_id && typeof p.source_id === 'number') {
            p.source_id = String(p.source_id);
        }
        
        // Validation: Requires Source, Category, AND (Numeric OR Text Value)
        const hasValue = p.value_numeric !== undefined || (p.value_text && p.value_text.trim().length > 0);
        const isValid = p.source_id && p.category_id && hasValue;
        
        if (!isValid) {
            console.warn(`[BulkInsert] Item ${index} dropped due to missing fields (Source/Category/Value):`, p);
        }
        return isValid;
    });

    if (validPoints.length === 0) {
        console.error("[BulkInsert] No valid data points to insert.");
        return [];
    }

    console.log(`[BulkInsert] Prepared ${validPoints.length} valid items.`);

    // 2. Prepare Network Request
    const functionUrl = `${PROJECT_URL}/functions/v1/bulk-insert-data`;
    
    // 3. Get Session Token with Timeout Protection and Backup Strategy
    console.log("[BulkInsert] Retrieving auth session...");
    let token = '';
    
    try {
        // Strategy: Race getSession vs getUser vs Timeout
        // Sometimes getSession hangs but getUser works if session is stale but valid
        const sessionPromise = supabase.auth.getSession().then(r => r.data.session?.access_token);
        const userPromise = supabase.auth.getUser().then(r => null); // getUser doesn't return token directly, just ensures checks.
        
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Auth Retrieval Timeout (2s)")), 2000)
        );

        // We primarily want the token from session
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        token = result as string || '';
        
        console.log(`[BulkInsert] Auth Token retrieved: ${token ? 'Yes (Authenticated)' : 'No (Anon Mode)'}`);
    } catch (e) {
        console.warn("[BulkInsert] Auth check failed or timed out. Proceeding with Anon Key.", e);
    }

    // Construct valid Authorization header.
    const authHeader = token ? `Bearer ${token}` : `Bearer ${ANON_KEY}`;

    console.log(`[BulkInsert] Sending POST request to: ${functionUrl}`);

    try {
        const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader,
                'apikey': ANON_KEY
            },
            body: JSON.stringify({ dataPoints: validPoints })
        });

        const responseText = await response.text();
        
        if (!response.ok) {
            console.error(`[BulkInsert] HTTP Error ${response.status}:`, responseText);
            throw new Error(`Server Error (${response.status}): ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            console.warn("[BulkInsert] Response was not JSON:", responseText);
            result = { success: validPoints.length }; // Fallback
        }

        console.log("[BulkInsert] Success:", result);
        
        // Return array of empty objects to match expected interface of inserted rows
        return new Array(result.success || validPoints.length).fill({});

    } catch (e: any) {
        console.error("[BulkInsert] Network/Execution Error:", e);
        throw e;
    }
};

// --- ANALYTICS ---

export const fetchDashboardStats = async () => {
    const { data, error } = await supabase.rpc('get_dashboard_stats');
    if (error) throw error;
    return data;
};

export const analyzePublicInsight = async (query: string) => {
    const { data, error } = await supabase.functions.invoke('public-insight-engine', {
        body: { query }
    });

    if (error) throw error;
    return data;
};

// --- EMBEDDING BACKFILL ---
// How many ledger points are missing an embedding (i.e. invisible to the oracle)?
export const countUnembeddedPublicData = async (): Promise<number> => {
    const { count, error } = await supabase
        .from('public_data_points')
        .select('id', { count: 'exact', head: true })
        .is('embedding', null);
    if (error) throw error;
    return count || 0;
};

// Embed every ledger point that is missing an embedding, in chunks, until done.
// Reports progress via onProgress({ embedded, remaining }). Returns total embedded.
export const backfillPublicEmbeddings = async (
    onProgress?: (p: { embedded: number; remaining: number }) => void
): Promise<{ embedded: number; skipped: number; failures: { id: string; reason: string }[] }> => {
    let totalEmbedded = 0;
    let totalSkipped = 0;
    const failures: { id: string; reason: string }[] = [];
    // Loop chunk-by-chunk. Guard with a generous cap so a stuck backend can't spin forever.
    for (let i = 0; i < 500; i++) {
        const { data, error } = await supabase.functions.invoke('embed-pending-public-data', {
            body: { limit: 100 },
        });
        if (error) throw error;
        const embedded = data?.embedded || 0;
        const skipped = data?.skipped || 0;
        const remaining = data?.remaining || 0;
        totalEmbedded += embedded;
        totalSkipped += skipped;
        if (Array.isArray(data?.failures)) for (const f of data.failures) if (failures.length < 5) failures.push(f);
        onProgress?.({ embedded: totalEmbedded, remaining });
        // Stop when nothing left, or when a chunk made no progress (only unembeddable rows remain).
        if (remaining === 0 || (embedded === 0 && skipped === 0)) break;
        if (embedded === 0 && skipped > 0 && skipped >= remaining) break;
    }
    return { embedded: totalEmbedded, skipped: totalSkipped, failures };
};
